import { PUSH_API_URL, VAPID_PUBLIC_KEY } from './config';
import { urlBase64ToUint8Array } from './badge';
import { iconSvg } from './ui/icon';
import { HEADER_ICON } from './ui/header';

/**
 * Web Push opt-in + home-screen badge, all self-contained. Injects a bell into
 * the header (rather than threading push through header.ts), registers the
 * service worker, and manages the subscription lifecycle.
 *
 * Dormant until VAPID_PUBLIC_KEY is set: with no key there is nothing to
 * subscribe to, so nothing is registered or rendered and the site is unchanged.
 */

type BellState = 'default' | 'subscribed' | 'needs-install';

const isStandalone = (): boolean =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari exposes this non-standard flag on installed Home Screen apps.
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ reports a desktop UA; the touch-point check catches it.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const pushSupported = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/**
 * Clear the home-screen badge. Called on load and whenever the installed app is
 * refocused — opening lab means the new items are being looked at.
 */
export function markSeen(): void {
  navigator.clearAppBadge?.().catch(() => {});
}

export function initPush(): void {
  if (!VAPID_PUBLIC_KEY) return; // feature not provisioned yet — stay dormant
  void setup();
}

async function setup(): Promise<void> {
  markSeen();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') markSeen();
  });

  // iOS only allows push from an installed Home Screen app. Before install,
  // PushManager isn't even present — surface a hint instead of a dead button.
  if (!pushSupported()) {
    if (isIos() && !isStandalone()) renderBell('needs-install');
    return;
  }

  let reg: ServiceWorkerRegistration;
  try {
    reg = await navigator.serviceWorker.register('/sw.js');
  } catch {
    return; // no SW ⇒ no push; leave the header untouched
  }

  if (Notification.permission === 'denied') return; // blocked — don't nag

  const existing = await reg.pushManager.getSubscription().catch(() => null);
  renderBell(existing ? 'subscribed' : 'default', reg);
}

let bellEl: HTMLButtonElement | null = null;
let hintEl: HTMLElement | null = null;
// Guards against a double-tap racing two subscribe/unsubscribe calls.
let busy = false;

function renderBell(state: BellState, reg?: ServiceWorkerRegistration): void {
  const controls = document.querySelector('.header-controls');
  if (!controls) return;

  if (!bellEl) {
    bellEl = document.createElement('button');
    bellEl.className = 'text-link text-link-quiet theme-toggle push-bell';
    bellEl.type = 'button';
    controls.insertBefore(bellEl, controls.querySelector('#surprise'));
  }

  // HEADER_ICON, not a literal: the bell is injected into the middle of the masthead cluster, so a
  // size of its own would make it visibly the odd one out.
  bellEl.innerHTML = iconSvg('bell', HEADER_ICON);
  bellEl.classList.toggle('push-bell--on', state === 'subscribed');

  const labels: Record<BellState, string> = {
    default: 'Get notified when a new tool ships',
    subscribed: 'New-tool alerts are on — tap to turn off',
    'needs-install': 'Add lab to your Home Screen to get new-tool alerts',
  };
  bellEl.title = labels[state];
  bellEl.setAttribute('aria-label', labels[state]);
  bellEl.setAttribute('aria-pressed', String(state === 'subscribed'));

  bellEl.onclick = async () => {
    if (state === 'needs-install') {
      toggleInstallHint();
      return;
    }
    if (busy) return; // ignore taps while a subscribe/unsubscribe is in flight
    busy = true;
    bellEl!.disabled = true;
    try {
      if (state === 'subscribed') await unsubscribe(reg!);
      else await subscribe(reg!);
    } finally {
      busy = false;
      if (bellEl) bellEl.disabled = false;
    }
  };
}

function toggleInstallHint(): void {
  if (hintEl) {
    closeInstallHint();
    return;
  }
  if (!bellEl) return;
  hintEl = document.createElement('div');
  hintEl.className = 'push-popover';
  hintEl.setAttribute('role', 'status');
  hintEl.innerHTML =
    '<span class="push-popover-arrow" aria-hidden="true"></span>' +
    'To get alerts, add <strong>lab</strong> to your Home Screen: tap ' +
    '<strong>Share</strong> → <strong>Add to Home Screen</strong>, open it from there, ' +
    'then tap the bell again.';
  document.body.appendChild(hintEl);
  positionInstallHint();
  requestAnimationFrame(() => {
    // If it was closed before this frame ran, don't attach a listener that
    // closeInstallHint already tried (and failed) to remove.
    if (!hintEl) return;
    hintEl.classList.add('push-popover--in');
    // Deferred so the tap that opened the popover doesn't immediately close it.
    document.addEventListener('click', onDocClickForHint, true);
  });
  window.addEventListener('resize', positionInstallHint);
  window.addEventListener('scroll', positionInstallHint, true);
  document.addEventListener('keydown', onKeyForHint);
}

function positionInstallHint(): void {
  if (!hintEl || !bellEl) return;
  const r = bellEl.getBoundingClientRect();
  const right = Math.max(8, window.innerWidth - r.right);
  hintEl.style.top = `${Math.round(r.bottom + 8)}px`;
  hintEl.style.right = `${Math.round(right)}px`;
  const arrow = hintEl.querySelector<HTMLElement>('.push-popover-arrow');
  if (arrow) arrow.style.right = `${Math.max(10, Math.round(r.width / 2 - 5))}px`;
}

function closeInstallHint(): void {
  if (!hintEl) return;
  hintEl.remove();
  hintEl = null;
  document.removeEventListener('click', onDocClickForHint, true);
  window.removeEventListener('resize', positionInstallHint);
  window.removeEventListener('scroll', positionInstallHint, true);
  document.removeEventListener('keydown', onKeyForHint);
}

function onDocClickForHint(e: MouseEvent): void {
  const t = e.target as Node;
  // Taps on the bell are handled by its own onclick (which toggles).
  if (hintEl?.contains(t) || bellEl?.contains(t)) return;
  closeInstallHint();
}

function onKeyForHint(e: KeyboardEvent): void {
  if (e.key === 'Escape') closeInstallHint();
}

async function subscribe(reg: ServiceWorkerRegistration): Promise<void> {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    const res = await fetch(`${PUSH_API_URL}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sub),
    });
    if (!res.ok) throw new Error(`subscribe: HTTP ${res.status}`);

    renderBell('subscribed', reg);
  } catch {
    // Roll the subscription back so we don't leave a local sub the server
    // never stored, then return to the un-subscribed state.
    await reg.pushManager
      .getSubscription()
      .then((s) => s?.unsubscribe())
      .catch(() => {});
    renderBell('default', reg);
  }
}

async function unsubscribe(reg: ServiceWorkerRegistration): Promise<void> {
  try {
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await fetch(`${PUSH_API_URL}/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      }).catch(() => {});
      await sub.unsubscribe();
    }
  } finally {
    renderBell('default', reg);
  }
}
